/**
 * Email and Name validation utility functions
 */

/**
 * Improved email validation regex based on RFC 5322 specification
 * This regex is more efficient and comprehensive than the basic pattern
 *
 * Features:
 * - Supports all valid email characters in local part
 * - Validates domain names with proper length constraints
 * - Handles subdomain validation
 * - More efficient than complex RFC 5322 full compliance regex
 */
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;

/**
 * Validates an email address
 * @param email - The email string to validate
 * @returns boolean indicating if the email is valid
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const trimmedEmail = email.trim();

  // Check length constraints
  if (trimmedEmail.length === 0 || trimmedEmail.length > 254) {
    return false;
  }

  // Check for basic structure before regex
  const atIndex = trimmedEmail.indexOf('@');
  if (atIndex === -1 || atIndex === 0 || atIndex === trimmedEmail.length - 1) {
    return false;
  }

  // Check if there's only one @ symbol
  if (trimmedEmail.indexOf('@', atIndex + 1) !== -1) {
    return false;
  }

  // Use the regex for detailed validation
  return EMAIL_REGEX.test(trimmedEmail);
};

/**
 * Validates email and returns error message if invalid
 * @param email - The email string to validate
 * @param fieldName - Optional field name for error message
 * @returns Empty string if valid, error message if invalid
 */
export const validateEmail = (email: string, fieldName: string = 'Email'): string => {
  if (!email.trim()) {
    return `${fieldName} is required`;
  }

  if (!isValidEmail(email)) {
    return 'Enter email in the correct format, like name@email.com';
  }

  return '';
};

/**
 * Name validation utility functions
 */

/**
 * Regex patterns for name validation
 */
export const NAME_PATTERNS = {
  NUMBERS: /\d/,
  SPECIAL_CHARS: /[!@#$%^&*(),.?":{}|<>]/,
  VALID_NAME: /^[a-zA-Z\s'-]+$/,
} as const;

/**
 * Validates a name field
 * @param name - The name string to validate
 * @returns boolean indicating if the name is valid
 */
export const isValidName = (name: string): boolean => {
  if (!name || typeof name !== 'string') {
    return false;
  }

  const trimmedName = name.trim();

  // Check if empty
  if (trimmedName.length === 0) {
    return false;
  }

  // Check for numbers
  if (NAME_PATTERNS.NUMBERS.test(trimmedName)) {
    return false;
  }

  // Check for special characters (excluding allowed ones like apostrophes and hyphens)
  if (NAME_PATTERNS.SPECIAL_CHARS.test(trimmedName)) {
    return false;
  }

  // Additional validation: should only contain letters, spaces, apostrophes, and hyphens
  return NAME_PATTERNS.VALID_NAME.test(trimmedName);
};

/**
 * Validates a name and returns error message if invalid
 * @param name - The name string to validate
 * @param fieldName - The field name for error message (e.g., 'First name', 'Last name')
 * @returns Empty string if valid, error message if invalid
 */
export const validateName = (name: string, fieldName: string): string => {
  if (!name.trim()) {
    return `${fieldName} is required`;
  }
  return '';
};

export const validatechatbotContactReason = (reason: string): string => {
  if (!reason.trim()) return 'Contact reason is required';
  if (reason.length > 255) return 'Contact reason should not exceed 255 characters';
  return '';
};

export const validateOrderNumber = (orderNumber: string): string => {
  if (!orderNumber.trim()) {
    return 'Order number is required';
  }

  if (!orderNumber.trim().toUpperCase().startsWith('VP')) {
    return 'Enter order number in the correct format, like VP_123456';
  }
  return '';
};
